import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, BrainCircuit, Users, Truck, BarChart3, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Home = () => {
  const features = [
    {
      icon: ShieldCheck,
      title: "Verified Profiles",
      description: "Automated badge verification with TWIC, TSA, and Hazmat credentials."
    },
    {
      icon: BrainCircuit,
      title: "AI-Powered Matching",
      description: "Intelligent job matching based on your skills, location, and certifications."
    },
    {
      icon: Users,
      title: "Mentorship Network",
      description: "Connect with experienced carriers and industry experts for guidance."
    },
    {
      icon: BarChart3,
      title: "Resource Library",
      description: "Access curated guides, compliance tools, and grant opportunities."
    },
    {
      icon: Truck,
      title: "Job Board",
      description: "Browse high-paying opportunities from verified dispatchers and companies."
    },
    {
      icon: Zap,
      title: "Referral Rewards",
      description: "Grow your network and earn rewards for bringing in verified carriers."
    }
  ];

  const stats = [];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1750969185331-e03829f72c7d" 
            alt="AI Network" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-slate-900/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Built for the{' '}
              <span className="text-gradient">Box Truck Industry</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              StraightConnect helps verified trucking professionals connect safely, build relationships, access resources, and grow their businesses with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/join"
                className="bg-orange-500 text-white px-8 py-4 rounded-md font-bold text-lg hover:bg-orange-600 transition-all btn-hover-lift shadow-lg"
                data-testid="hero-join-btn"
              >
                Create Your Verified Profile
                <ArrowRight className="inline ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/resource-hub"
                className="bg-white text-slate-900 px-8 py-4 rounded-md font-semibold text-lg hover:bg-slate-100 transition-all border border-slate-200"
                data-testid="hero-resources-btn"
              >
                Explore Resources
              </Link>
            </div>
          </motion.div>

          {/* Mission Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
          >
            {[
              { value: "Verified", label: "Networking & Connections" },
              { value: "Education", label: "Resources & Training" },
              { value: "Transparency", label: "Reducing Fraud" },
              { value: "Growth", label: "Small Business Support" }
            ].map((stat) => (
              <div key={stat.label} className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-md" data-testid={`stat-${stat.label}`}>
                <div className="font-heading text-3xl font-bold text-blue-400">{stat.value}</div>
                <div className="text-sm text-slate-300 mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trust Layer */}
      <section className="bg-white py-12 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-600 font-medium mb-6">
            Integrated with industry platforms and compliance networks including
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <div className="text-2xl font-bold text-slate-400">Federal Motor Carrier Safety Administration</div>
            <div className="text-2xl font-bold text-slate-400">RMIS</div>
            <div className="text-2xl font-bold text-slate-400">Highway</div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="bg-slate-50 py-12 border-b border-slate-200" data-testid="quick-links-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Quick Links</h3>
              <p className="text-sm text-slate-500 mt-1">Jump straight to what you need.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: '/resource-hub', label: 'Resource Hub', desc: 'Free guides for drivers, carriers & dispatchers.', emoji: '📚' },
              { to: '/external-job-boards', label: 'Job Boards', desc: 'Browse verified load boards.', emoji: '🚛' },
              { to: '/about', label: 'About', desc: 'Our mission & how the network works.', emoji: 'ℹ️' },
              { to: null, label: 'Affiliate Hub', desc: 'Verified Partners. Real Resources. Real Earnings.', emoji: '🤝', accent: true, comingSoon: true },
            ].map(link => {
              const inner = (
                <>
                  {link.comingSoon && (
                    <span className="absolute top-3 right-3 px-2 py-0.5 bg-white/20 backdrop-blur border border-white/30 text-[10px] font-bold tracking-wider uppercase rounded text-white">
                      Coming Soon
                    </span>
                  )}
                  <div className="text-2xl mb-2">{link.emoji}</div>
                  <h4 className={`font-bold mb-1 ${link.accent ? 'text-white' : 'text-slate-900'}`}>{link.label}</h4>
                  <p className={`text-xs leading-relaxed ${link.accent ? 'text-orange-50' : 'text-slate-500'}`}>{link.desc}</p>
                  {!link.comingSoon && (
                    <span className={`inline-flex items-center gap-1 mt-3 text-xs font-semibold ${link.accent ? 'text-white' : 'text-blue-600'} opacity-0 group-hover:opacity-100 transition-opacity`}>
                      Explore <ArrowRight className="h-3 w-3" />
                    </span>
                  )}
                </>
              );
              const baseCls = `group relative block p-5 rounded-xl border transition-all ${link.accent ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-600' : 'bg-white border-slate-200 hover:border-blue-300'} ${link.comingSoon ? 'cursor-default opacity-95' : 'hover:-translate-y-0.5 hover:shadow-lg ' + (link.accent ? 'hover:shadow-orange-500/30' : '')}`;
              return link.comingSoon ? (
                <div key={link.label} className={baseCls} data-testid={`quick-link-${link.label.toLowerCase().replace(/ /g, '-')}`} aria-disabled="true">
                  {inner}
                </div>
              ) : (
                <Link key={link.to} to={link.to} data-testid={`quick-link-${link.label.toLowerCase().replace(/ /g, '-')}`} className={baseCls}>
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ecosystem Features
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to succeed as a verified non-CDL carrier
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 rounded-md p-6 hover:border-blue-200 transition-colors card-hover"
                data-testid={`feature-card-${index}`}
              >
                <div className="bg-blue-50 w-12 h-12 rounded-md flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem-Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                The Challenges You Face
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="bg-red-100 p-1 rounded-full mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Fragmentation</h4>
                    <p className="text-slate-600">Scattered information and unreliable sources</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-red-100 p-1 rounded-full mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Lack of Education</h4>
                    <p className="text-slate-600">Hard to find compliance and business guidance</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-red-100 p-1 rounded-full mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Verification Gaps</h4>
                    <p className="text-slate-600">No standardized trust system for carriers</p>
                  </div>
                </li>
              </ul>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1761590542271-27a3518ab957" 
                alt="Sprinter van on highway" 
                className="rounded-md shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Don't Drive in the Dark
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join the verified standard today and unlock opportunities across the network.
          </p>
          <Link
            to="/join"
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-md font-bold text-lg hover:bg-blue-50 transition-all btn-hover-lift shadow-xl"
            data-testid="cta-join-btn"
          >
            Create Your Verified Profile
            <ArrowRight className="inline ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;